import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getRole, getUsername } from "@/lib/auth";

interface NavItem {
  icon: string;
  label: string;
  path?: string;
  children?: { icon: string; label: string; path: string }[];
  // 不写 roles 则默认仅 admin 可见；写了就按数组允许。
  roles?: ("admin" | "ops")[];
}

const navItems: NavItem[] = [
  { icon: "ri-dashboard-line", label: "概览看板", path: "/dashboard" },
  { icon: "ri-hand-coin-line", label: "租借归还", path: "/rental", roles: ["admin", "ops"] },
  {
    icon: "ri-book-2-line",
    label: "三峡专属知识库",
    path: "/knowledge",
    children: [
      { icon: "ri-route-line", label: "游线导览", path: "/knowledge/routes" },
      { icon: "ri-map-2-line", label: "点位内容", path: "/knowledge/scenes" },
      { icon: "ri-volume-up-line", label: "讲解音频", path: "/knowledge/audio" },
      { icon: "ri-questionnaire-line", label: "预设话术", path: "/knowledge/global-qa" },
      { icon: "ri-image-2-line", label: "媒体库", path: "/knowledge/media-assets" },
      { icon: "ri-film-line", label: "视频图片", path: "/videos" },
      { icon: "ri-database-2-line", label: "知识全库", path: "/knowledge/library" },
    ],
  },
  { icon: "ri-image-edit-line", label: "AUGC换脸", path: "/faceswap" },
  { icon: "ri-file-paper-line", label: "旅游报告", path: "/reports" },
  {
    icon: "ri-terminal-box-line",
    label: "运维管理",
    path: "/ops",
    children: [
      { icon: "ri-glasses-line", label: "设备管理", path: "/devices" },
      { icon: "ri-suitcase-line", label: "游客与行程", path: "/trips" },
      { icon: "ri-bar-chart-box-line", label: "数据分析", path: "/analytics" },
      { icon: "ri-route-line", label: "外采数据", path: "/tracking" },
      { icon: "ri-record-circle-line", label: "讲解点采集", path: "/capture" },
      { icon: "ri-book-read-line", label: "运维知识库", path: "/ops/knowledge" },
      { icon: "ri-file-list-3-line", label: "运维报告", path: "/ops/reports" },
      { icon: "ri-settings-4-line", label: "系统配置", path: "/settings" },
    ],
  },
];

function Sidebar({ onCloseMobile }: { onCloseMobile?: () => void }) {
  const location = useLocation();
  const role = getRole();
  const username = getUsername();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    "三峡专属知识库": true,
  });

  // ops 角色只看到显式允许的菜单项；admin 看全部。
  const visibleItems = navItems.filter(
    (item) => role === "admin" || (item.roles?.includes(role) ?? false),
  );

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <aside className="h-screen w-64 bg-white border-r border-gray-200 flex flex-col">
      {/* Brand Header (click to go home) */}
      <div className="h-16 flex items-center px-4 border-b border-gray-200 gap-3">
        <a href="/" className="flex items-center gap-3 min-w-0 hover:opacity-80 transition-opacity">
          <div className="w-9 h-9 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
            <i className="ri-glasses-line text-white text-lg"></i>
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-bold text-gray-900 leading-tight truncate">三峡 · 两坝一峡</span>
            <span className="text-xs text-gray-500 leading-tight">导览运营台</span>
          </div>
        </a>
        {/* Mobile close button */}
        {onCloseMobile && (
          <button
            onClick={onCloseMobile}
            className="lg:hidden ml-auto w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        {visibleItems.map((item) => {
          const isActive = item.path ? location.pathname === item.path : false;
          const isParentActive = item.children ? item.children.some((c) => location.pathname === c.path) : false;
          // group counts as expanded when its own overview page is active too
          const isExpanded =
            expandedMenus[item.label] !== false &&
            (expandedMenus[item.label] ||
              isParentActive ||
              (!!item.path && !!item.children && location.pathname === item.path));

          if (item.children) {
            // 当父分组本身有 path 时，主区是 Link（点击跳总览），右侧箭头是
            // 独立 button（点击只展开/收起），不再共用一个 button 以避免歧义。
            const groupActive = !!item.path && location.pathname === item.path;
            const headerCls = `flex items-center justify-between rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              isParentActive || groupActive
                ? "bg-teal-50 text-teal-700"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            }`;
            return (
              <div key={item.label} className="mb-1">
                {item.path ? (
                  <div className={headerCls}>
                    <Link
                      to={item.path}
                      onClick={() => setExpandedMenus((prev) => ({ ...prev, [item.label]: true }))}
                      className="flex items-center gap-3 px-3 py-2.5 flex-1 min-w-0"
                    >
                      <i className={`${item.icon} text-lg w-5 flex items-center justify-center`}></i>
                      {item.label}
                    </Link>
                    <button
                      onClick={() => setExpandedMenus((prev) => ({ ...prev, [item.label]: !isExpanded }))}
                      aria-label={isExpanded ? "收起" : "展开"}
                      className="px-2 py-2.5 hover:bg-black/5 rounded-r-lg"
                    >
                      <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}></i>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => toggleMenu(item.label)}
                    className={`w-full px-3 py-2.5 ${headerCls}`}
                  >
                    <div className="flex items-center gap-3">
                      <i className={`${item.icon} text-lg w-5 flex items-center justify-center`}></i>
                      {item.label}
                    </div>
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}></i>
                  </button>
                )}
                {isExpanded && (
                  <div className="ml-3 mt-0.5 space-y-0.5">
                    {item.children.map((child) => {
                      const childActive = location.pathname === child.path;
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                            childActive
                              ? "bg-teal-50 text-teal-700"
                              : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                          }`}
                        >
                          <i className={`${child.icon} text-base w-5 flex items-center justify-center`}></i>
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-1 whitespace-nowrap ${
                isActive
                  ? "bg-teal-50 text-teal-700"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <i className={`${item.icon} text-lg w-5 flex items-center justify-center`}></i>
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full">
            <i className="ri-user-3-line text-amber-700 text-sm"></i>
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-medium text-gray-900 truncate">
              {role === "ops" ? "运营人员" : "管理员"}
            </span>
            <span className="text-xs text-gray-500 truncate">{username || "—"}</span>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;